import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
  themePreference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setThemePreference: (pref: ThemePreference) => void;
  resolveFromSystem: () => void;
}

function resolve(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') {
    return typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return pref;
}

function apply(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  // iOS standalone status-bar tint follows theme.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = theme === 'light' ? '#fffbf5' : '#102a43';
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      themePreference: 'system',
      resolvedTheme: resolve('system'),
      setThemePreference: (pref) => {
        const resolved = resolve(pref);
        apply(resolved);
        set({ themePreference: pref, resolvedTheme: resolved });
      },
      resolveFromSystem: () => {
        const { themePreference } = get();
        if (themePreference !== 'system') return;
        const resolved = resolve('system');
        apply(resolved);
        set({ resolvedTheme: resolved });
      },
    }),
    {
      name: 'bz-theme',
      // Only persist the user-chosen preference; resolvedTheme is derived.
      partialize: (s) => ({ themePreference: s.themePreference }),
      onRehydrateStorage: () => (s) => {
        if (s) {
          const resolved = resolve(s.themePreference);
          apply(resolved);
          s.resolvedTheme = resolved;
        }
      },
    },
  ),
);

/** Subscribe to OS-level theme changes and re-resolve when preference is 'system'. */
export function watchSystemTheme(): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => useThemeStore.getState().resolveFromSystem();
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}
